import { productPath } from '../services/productPath';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Heart, Loader2, ShoppingCart, Trash2, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { cartAPI, accountAlertsAPI } from '../services/api';
import { wishlistStore, useWishlistIds } from '../services/wishlistStore';
import { useT } from '../services/i18n';
import { prodName } from '../services/catalogI18n';
import BrandLogo from './BrandLogo';

const WishlistSection = () => {
  const { t, lang } = useT();
  const ids = useWishlistIds();
  const [products, setProducts] = useState(null);
  const [busy, setBusy] = useState(null);

  useEffect(() => {
    let cancelled = false;
    wishlistStore.fetchProducts().then(list => { if (!cancelled) setProducts(list); }).catch(() => !cancelled && setProducts([]));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids.join(',')]);

  const remove = async (id) => {
    setBusy(`rm-${id}`);
    try { await wishlistStore.toggle(id); } finally { setBusy(null); }
  };

  const addToCart = async (id) => {
    setBusy(`cart-${id}`);
    try {
      await cartAPI.addItem(id, 1);
      window.dispatchEvent(new Event('cart-updated'));
      toast.success('Ajouté au panier');
    } finally {
      setBusy(null);
    }
  };

  const priceAlerts = async () => {
    setBusy('alerts');
    try {
      const r = await accountAlertsAPI.wishlistPrice();
      toast.success(t('wl.priceAlertsDone', { n: r.created, a: r.already }));
    } catch { toast.error(t('alerts.error')); } finally { setBusy(null); }
  };

  const addAll = async () => {
    setBusy('all');
    try {
      for (const p of products) await cartAPI.addItem(p.id, 1);
      window.dispatchEvent(new Event('cart-updated'));
      toast.success(`${products.length} article(s) ajouté(s) au panier`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="bg-white rounded-xl shadow-sm p-6 lg:col-span-2" data-testid="wishlist-section">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2"><Heart size={20} className="text-red-500 fill-red-500" /> {t('wl.title')} <span className="text-sm font-normal text-gray-500" data-testid="wishlist-count">({ids.length})</span></h2>
        <div className="flex flex-wrap items-center gap-2">
        {products && products.length > 0 && !!localStorage.getItem('token') && (
          <Button size="sm" variant="outline" data-testid="wishlist-price-alerts" onClick={priceAlerts} disabled={busy === 'alerts'} title={t('wl.priceAlertsHint')} className="border-purple-300 text-purple-800 hover:bg-purple-50">
            {busy === 'alerts' ? <Loader2 size={14} className="mr-1 animate-spin" /> : <TrendingDown size={14} className="mr-1" />} {t('wl.priceAlerts')}
          </Button>
        )}
        {products && products.length > 1 && (
          <Button size="sm" data-testid="wishlist-add-all" onClick={addAll} disabled={busy === 'all'} className="bg-orange-500 hover:bg-orange-600">
            {busy === 'all' ? <Loader2 size={14} className="mr-1 animate-spin" /> : <ShoppingCart size={14} className="mr-1" />} Tout ajouter au panier
          </Button>
        )}
        </div>
      </div>
      {products === null ? (
        <div className="flex justify-center py-8"><Loader2 className="animate-spin text-orange-500" /></div>
      ) : products.length === 0 ? (
        <p className="text-sm text-gray-500" data-testid="wishlist-empty">{t('wl.empty')}</p>
      ) : (
        <ul className="divide-y divide-gray-100" data-testid="wishlist-list">
          {products.map(p => (
            <li key={p.id} data-testid={`wishlist-item-${p.id}`} className="flex items-center gap-4 py-3">
              <Link to={productPath(p)} className="w-16 h-16 flex items-center justify-center bg-gray-50 rounded flex-shrink-0">
                <img src={p.image} alt={prodName(p, lang)} className="max-h-14 max-w-14 object-contain" />
              </Link>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500"><BrandLogo brand={p.brand} size="xs" testId={`wishlist-brand-${p.id}`} /></p>
                <Link to={productPath(p)} className="font-medium text-gray-900 hover:text-orange-600 truncate block">{prodName(p, lang)}</Link>
                <p className="text-xs text-gray-400 font-mono">Réf. {p.ref}</p>
              </div>
              <p className="font-bold text-gray-900 whitespace-nowrap">{p.price.toFixed(2)} €</p>
              <Button size="sm" variant="outline" data-testid={`wishlist-cart-${p.id}`} onClick={() => addToCart(p.id)} disabled={busy === `cart-${p.id}`} className="hidden sm:inline-flex">
                {busy === `cart-${p.id}` ? <Loader2 size={14} className="animate-spin" /> : <ShoppingCart size={14} />}
              </Button>
              <button data-testid={`wishlist-remove-${p.id}`} onClick={() => remove(p.id)} disabled={busy === `rm-${p.id}`} className="p-2 text-red-600 hover:bg-red-50 rounded" title="Retirer">
                {busy === `rm-${p.id}` ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
};

export default WishlistSection;
